import React, { useState, useEffect, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import Loader from "../../../shared/ui/Loader.js";
import { useGlobalContext } from "../../../Config.js";
import {
  guardarSubproductoCompleto,
  cambiarEstadoSubproducto,
  getReporteSubproductos,
} from "../../../services/SubproductoService.js";
import { updateTipoSubproductoFicha } from "../../../services/SubproductoTypeService.js";
import TableSubproductos from "../components/TableSubproductos.js";
import FormCrearSubproducto from "../components/FormCrearSubproducto.js";
import FormEditarSubproducto from "../components/FormEditarSubproducto.js";
import useEmpresas from "../hooks/useEmpresas.js";
import useSubproductos from "../hooks/useSubproductos.js";
import useTiposSubproducto from "../hooks/useTiposSubproducto.js";
import {
  validarCamposSubproducto,
  esFichaJsonValida,
  existeSubproductoDuplicado,
} from "../utils/subproductoValidations.js";
import {
  construirPayload,
} from "../utils/subproductoHelpers.js";

const MantenedorSubproductos = () => {
  const toast = useRef(null);
  const { baseUrl } = useGlobalContext();

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [displayDialog, setDisplayDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedSubproducto, setSelectedSubproducto] = useState(null);
  const fichaRef = useRef(null);
  const [id, setId] = useState("");

  const [descripcion, setDescripcion] = useState("");
  const [tipo, setTipo] = useState(null);
  const [metraje, setMetraje] = useState(null);

  const { empresa, setEmpresa, listaEmpresas, nombreEmpresa } = useEmpresas(baseUrl);
  const { subproductos, loadingInicial, fetchData } = useSubproductos(
    baseUrl,
    empresa,
    toast
  );
  const { tipoOptions, fetchTipos } = useTiposSubproducto(baseUrl, empresa);

  const limpiarFormulario = () => {
    setDescripcion("");
    setTipo(null);
    setMetraje(null);
  };

  useEffect(() => {
    setGlobalFilterValue("");
    fetchData();
    fetchTipos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa, baseUrl]);

  const validarFormulario = (fichaVal = "") => {
    const errorCampos = validarCamposSubproducto({ descripcion, tipo, metraje });
    if (errorCampos) {
      toast.current?.show({
        severity: "warn",
        summary: "Atención",
        detail: errorCampos,
        life: 3000,
      });
      return false;
    }
    if (existeSubproductoDuplicado(subproductos, descripcion, selectedSubproducto?.id ?? null)) {
      toast.current?.show({
        severity: "warn",
        summary: "Atención",
        detail: "Ya existe un subproducto con esa descripción.",
        life: 3000,
      });
      return false;
    }
    if (!esFichaJsonValida(fichaVal)) {
      toast.current?.show({
        severity: "warn",
        summary: "Atención",
        detail: "La ficha debe ser un JSON válido.",
        life: 3000,
      });
      return false;
    }
    return true;
  };

  // ORQUESTADOR DE CREAR SUBPRODUCTO
  const handleCreateSubproducto = async (fichaData = null) => {
    const perfilFinal = fichaData?.perfil ?? "";
    const fichaFinal = fichaData?.fichaJson ?? "";
    if (!validarFormulario(fichaFinal)) return;
    try {
      setLoading(true);
      await guardarSubproductoCompleto(
        baseUrl,
        construirPayload({ empresa, tipo, descripcion, metraje, perfil: perfilFinal, ficha: fichaFinal })
      );
      toast.current?.show({
        severity: "success",
        summary: "Éxito",
        detail: "Subproducto creado exitosamente.",
        life: 3000,
      });
      limpiarFormulario();
      setDisplayDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error al crear subproducto:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Error al crear subproducto",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      await getReporteSubproductos(baseUrl);
      toast.current?.show({
        severity: "success",
        summary: "Exportación exitosa",
        detail: "Se descargó el archivo reporte_subproductos.xlsx",
        life: 3000,
      });
    } catch (error) {
      console.error("Error al exportar reporte de subproductos:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "No se pudo exportar el archivo Excel.",
        life: 3000,
      });
    } finally {
      setExporting(false);
    }
  };

  const handleEditSubproducto = (rowData) => {
    setSelectedSubproducto(rowData);
    setId(rowData.id);
    setDescripcion(rowData.descripcion || "");
    setTipo(rowData.id_tipo || null);
    setMetraje(rowData.metraje !== null && rowData.metraje !== undefined ? rowData.metraje : null);
    setShowEditDialog(true);
  };

  // ORQUESTADOR DE EDITAR SUBPRODUCTO
  const handleSaveSubproducto = async (fichaData = null) => {
    const perfilFinal = fichaData?.perfil ?? "";
    const fichaFinal = fichaData?.fichaJson ?? "";
    if (!validarFormulario(fichaFinal)) return;
    try {
      setLoading(true);
      await guardarSubproductoCompleto(
        baseUrl,
        construirPayload({
          empresa: selectedSubproducto.id_empresa ?? empresa,
          tipo,
          descripcion,
          metraje,
          idSubproducto: selectedSubproducto.id,
        })
      );

      // Actualizar/crear la ficha técnica (upsert) si se editó el paso de ficha
      if (fichaData && perfilFinal && fichaFinal) {
        await updateTipoSubproductoFicha(baseUrl, {
          idSubproducto: selectedSubproducto.id,
          perfil: perfilFinal,
          ficha: fichaFinal,
        });
      }

      toast.current?.show({
        severity: "success",
        summary: "Éxito",
        detail: "Subproducto actualizado exitosamente.",
        life: 3000,
      });
      limpiarFormulario();
      setSelectedSubproducto(null);
      setId("");
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error al actualizar subproducto:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Error al actualizar subproducto",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEstadoSubproducto = (rowData) => {
    const activo = rowData.activo === true || rowData.activo === 1;
    confirmDialog({
      message: activo
        ? `¿Está seguro de que desea desactivar el subproducto "${rowData.descripcion}"?`
        : `¿Está seguro de que desea activar el subproducto "${rowData.descripcion}"?`,
      header: "Confirmar Cambio de Estado",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sí",
      rejectLabel: "No",
      accept: async () => {
        try {
          await cambiarEstadoSubproducto(baseUrl, rowData.id, !activo);
          toast.current?.show({
            severity: "success",
            summary: "Éxito",
            detail: activo
              ? "Subproducto desactivado exitosamente."
              : "Subproducto activado exitosamente.",
            life: 3000,
          });
          fetchData();
        } catch (error) {
          console.error("Error al cambiar estado del subproducto:", error);
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: error.message || "Error al cambiar estado del subproducto",
            life: 3000,
          });
        }
      },
    });
  };

  const onGlobalFilterChange = (e) => {
    setGlobalFilterValue(e.target.value);
  };

  const renderHeader = () => {
    return (
      <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2">
        <span
          className="p-input-icon-left"
          style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <i className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Buscar por Descripción"
          />
        </span>
        <span
          className="p-input-icon-left ml-2"
          style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
        >
          {subproductos?.length} subproductos {nombreEmpresa || "TODAS"}
        </span>
      </div>
    );
  };
  const header = renderHeader();

  const subproductosConEmpresa = (subproductos || []).map((s) => ({
    ...s,
    nombre_empresa:
      listaEmpresas.find((e) => e.id === s.id_empresa)?.nombre || "-",
  }));

  return (
    <div className="grid">
      <Toast ref={toast} />
      <div className="col-12">
        <div className="card">
          <h4>Gestión de Subproductos</h4>
          <div className="p-field">
            <label htmlFor="empresa">Seleccione Empresa: </label>
            <Dropdown
              id="empresa"
              value={empresa}
              onChange={(e) => setEmpresa(e.value)}
              options={[{ id: null, nombre: "TODAS" }, ...listaEmpresas]}
              optionLabel="nombre"
              optionValue="id"
              placeholder="Seleccione Empresa"
              className="p-inputtext-sm"
              style={{ borderRadius: "4px" }}
            />
          </div>
        </div>
      </div>
      <div className="col-12">
        <div className="card">
          {loadingInicial ? (
            <Loader />
          ) : (
            <>
              <Button
                label="Crear Subproducto"
                icon="pi pi-plus"
                className="p-button p-component mr-2 p-button-rounded p-button-primary mt-3 mb-3"
                onClick={() => {
                  limpiarFormulario();
                  setDisplayDialog(true);
                }}
                disabled={loadingInicial || !empresa}
                raised
              />

              <Button
                label="Exportar Excel"
                icon="pi pi-file-excel"
                className="p-button p-component mr-2 p-button-rounded p-button-success mt-3 mb-3"
                onClick={handleExportExcel}
                loading={exporting}
                disabled={loadingInicial}
                raised
              />

              <TableSubproductos
                header={header}
                subproductos={subproductosConEmpresa}
                loading={loadingInicial}
                globalFilterValue={globalFilterValue}
                handleEditSubproducto={handleEditSubproducto}
                handleChangeEstadoSubproducto={handleChangeEstadoSubproducto}
                nombreEmpresa={nombreEmpresa || "TODAS"}
                className="mt-3"
              />

              <FormCrearSubproducto
                displayDialog={displayDialog}
                setDisplayDialog={setDisplayDialog}
                descripcion={descripcion}
                setDescripcion={setDescripcion}
                tipo={tipo}
                setTipo={setTipo}
                metraje={metraje}
                setMetraje={setMetraje}
                tipoOptions={tipoOptions}
                handleCreateSubproducto={handleCreateSubproducto}
                loading={loading}
              />

              <FormEditarSubproducto
                showEditDialog={showEditDialog}
                setShowEditDialog={setShowEditDialog}
                selectedSubproducto={selectedSubproducto}
                descripcion={descripcion}
                setDescripcion={setDescripcion}
                tipo={tipo}
                setTipo={setTipo}
                metraje={metraje}
                setMetraje={setMetraje}
                tipoOptions={tipoOptions}
                handleSaveSubproducto={handleSaveSubproducto}
                fichaRef={fichaRef}
                id={id}
                loading={loading}
              />

              <ConfirmDialog />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MantenedorSubproductos;
